import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { TuiAlertService } from '@taiga-ui/core';
import { finalize, take } from 'rxjs';
import { CancelServiceService } from 'src/app/shared/modal-cancel-service/service/cancel-service.service';
import {
  FailedServiceOption,
  FailedServicePayload,
  FailedServiceService,
} from './failed-service.service';

@Component({
  selector: 'app-modal-failed-service',
  templateUrl: './modal-failed-service.component.html',
  styleUrls: ['./modal-failed-service.component.css'],
})
export class ModalFailedServiceComponent {
  @Input({ required: true }) orderHash!: string;
  @Input() nodeId!: string;
  @Input() autoSave: boolean = true;

  @Output() reasonSelected = new EventEmitter<{
    reasonId: string;
    reasonText: string | null;
  }>();

  form: FormGroup;
  reasonsLoading: boolean = false;
  saving: boolean = false;

  reasons: FailedServiceOption[] = [];
  otherReasonGuid: string | null = null;
  otherReasonCodes: string[] = ['OTHER_REASON', 'OTHER'];

  constructor(
    private readonly fb: FormBuilder,
    private readonly activeModal: NgbActiveModal,
    private readonly cancelService: CancelServiceService,
    private readonly translate: TranslateService,
    private readonly alertService: TuiAlertService,
    private readonly failedService: FailedServiceService
  ) {
    this.form = this.fb.group({
      reasonId: [null, Validators.required],
      reasonText: [''],
    });
    this.setupDynamicValidators();
    this.loadReasons();
  }

  private setupDynamicValidators(): void {
    this.form.get('reasonId')!.valueChanges.subscribe((value) => {
      const reasonTextCtrl = this.form.get('reasonText')!;

      if (value === this.otherReasonGuid) {
        reasonTextCtrl.setValue('');
        reasonTextCtrl.setValidators([Validators.required]);
      } else {
        reasonTextCtrl.clearValidators();
      }

      reasonTextCtrl.updateValueAndValidity();
    });
  }

  private loadReasons(): void {
    this.reasonsLoading = true;

    this.failedService
      .getFailedReasons()
      .pipe(
        take(1),
        finalize(() => {
          this.reasonsLoading = false;
        })
      )
      .subscribe({
        next: (reasons: FailedServiceOption[]) => {
          this.reasons = reasons ?? [];
          this.setOtherReasonGuid(this.reasons);
        },
        error: (error: unknown) => {
          console.error('[ModalCancelService] loadReasons error for', error);

          this.reasons = [];

          const msg = this.translate.instant(
            'service_manager.failed.error_loading_reasons'
          );

          this.alertService
            .open(msg, { status: 'error', autoClose: true })
            .subscribe();
        },
      });
  }

  private setOtherReasonGuid(reasons: FailedServiceOption[] | null): void {
    const match = reasons?.find((reason) =>
      this.otherReasonCodes.includes(reason.code)
    );
    this.otherReasonGuid = match ? match.id : null;
  }

  close(): void {
    this.activeModal.dismiss();
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { reasonId, reasonText } = this.form.value;
    const customReasonText =
      reasonId === this.otherReasonGuid && typeof reasonText === 'string'
        ? reasonText.trim()
        : null;

    if (!this.autoSave) {
      this.reasonSelected.emit({
        reasonId,
        reasonText: customReasonText,
      });
      this.activeModal.close('saved');
      return;
    }

    const payload: FailedServicePayload = {
      FailureReasonGuid: reasonId,
      NodeId: this.nodeId,
      OtherReason: customReasonText,
    };

    this.saving = true;
    this.failedService
      .markAsFailted(this.orderHash, payload)
      .pipe(
        take(1),
        finalize(() => (this.saving = false))
      )
      .subscribe({
        next: () => {
          this.reasonSelected.emit({
            reasonId,
            reasonText: customReasonText,
          });
          this.activeModal.close('saved');
        },
        error: (err: unknown) => {
          console.error('[ModalCancelService] save error', err);

          const base = this.translate.instant(
            'service_manager.failed.failed_error'
          );
          const extra =
            typeof err === 'object' && err && 'message' in err
              ? (err as { message: string }).message
              : '';
          const msg = extra ? `${base}: ${extra}` : base;

          this.alertService
            .open(msg, { status: 'error', autoClose: true })
            .subscribe();
        },
      });
  }
}
